import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, ListFilter, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Empty, Eyebrow, FilterSelect, Input, Skeleton } from "../components/ui/primitives";
import { get } from "../lib/api";
import { cn } from "../lib/cn";
import { useCompany } from "../lib/company";
import { fmtInt } from "../lib/format";
import {
  DEFAULT_PERIOD, PAGE_SIZE, PERIODS, PHASES, RUN_LABEL, STATUS_BY_VIEW, TRACE_KINDS, VIEW_HELP, VIEW_LABEL, VIEWS, readFilters, serverParams, writeFilters,
  type Filters, type RunsPage, type Summary, type TraceKind, type TracePage, type View,
} from "./agents/model";
import { RunDetailDialog } from "./agents/RunDetailDialog";
import { RunsTable } from "./agents/RunsTable";
import { TraceKinds, TraceTable } from "./agents/TraceView";
import { MissionsCenter } from "./agents/MissionsCenter";
import { MissionReconstructionDialog } from "./agents/MissionReconstructionDialog";

const SCROLL_KEY = "bgf-agents-scroll";
const clock = (ms: number) => (ms ? new Date(ms).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—");
const errText = (e: unknown) => (e instanceof Error ? e.message : "error desconocido");

function useNow(everyMs: number) {
  const [n, setN] = useState(() => Date.now());
  useEffect(() => { const t = setInterval(() => setN(Date.now()), everyMs); return () => clearInterval(t); }, [everyMs]);
  return n;
}

/** Ventana operativa de EJECUCIÓN: una fila por run. El avance de las misiones vive en el Kanban (Misiones). */
export function AgentsPage() {
  const { companyId } = useCompany();
  const [sp, setSp] = useSearchParams();
  const f = useMemo(() => readFilters(sp), [sp]);
  const now = useNow(15_000);
  const rootRef = useRef<HTMLDivElement>(null);
  const [showFilters, setShowFilters] = useState(false);

  /** Los cambios de filtro reemplazan la entrada del historial: «atrás» sale de la ventana, no recorre cada filtro. */
  const set = useCallback((patch: Partial<Filters>, o: { keepPage?: boolean } = {}) => {
    setSp((prev) => writeFilters({ ...readFilters(prev), ...(o.keepPage || "page" in patch ? {} : { page: 1 }), ...patch }), { replace: true });
  }, [setSp]);

  // El selector de empresa cambia el universo: un proyecto/repositorio de otra empresa dejaría la tabla vacía sin explicación.
  const prevCompany = useRef(companyId);
  useEffect(() => {
    if (prevCompany.current !== companyId) { prevCompany.current = companyId; if (f.project || f.repo || f.run || f.mission) set({ project: "", repo: "", run: "", mission: "" }); }
  }, [companyId, f.project, f.repo, f.run, f.mission, set]);

  // Búsqueda con debounce: escribir no dispara una consulta por tecla.
  const [qInput, setQInput] = useState(f.q);
  useEffect(() => { setQInput(f.q); }, [f.q]);
  useEffect(() => {
    if (qInput === f.q) return;
    const t = setTimeout(() => set({ q: qInput }), 350);
    return () => clearTimeout(t);
  }, [qInput, f.q, set]);

  const cid = companyId ? `&companyId=${companyId}` : "";
  const isTrace = f.view === "trace";
  const isMissions = f.view === "missions";
  const runsQ = useQuery({
    queryKey: ["agent-runs", companyId, f.view, f.q, f.project, f.repo, f.phase, f.provider, f.status, f.period, f.page],
    queryFn: () => get<RunsPage>(`/agents/runs?${serverParams(f, { withView: true })}&page=${f.page}&pageSize=${PAGE_SIZE}${cid}`),
    enabled: !isTrace && !isMissions, placeholderData: keepPreviousData, refetchInterval: 6_000,
  });
  const sumQ = useQuery({
    queryKey: ["agent-summary", companyId, f.q, f.project, f.repo, f.phase, f.provider, f.period],
    queryFn: () => get<Summary>(`/agents/summary?${serverParams(f, { withView: false })}${cid}`),
    placeholderData: keepPreviousData, refetchInterval: 6_000,
  });
  const sum = sumQ.data;
  const traceKind: TraceKind = f.kind || (sum ? (TRACE_KINDS.find((k) => sum.traceability.byKind[k] > 0) ?? "usage_without_run") : "usage_without_run");
  const traceQ = useQuery({
    queryKey: ["agent-trace", companyId, traceKind, f.page],
    queryFn: () => get<TracePage>(`/agents/trace?kind=${traceKind}&page=${f.page}&pageSize=${PAGE_SIZE}${cid}`),
    enabled: isTrace, placeholderData: keepPreviousData, refetchInterval: 6_000,
  });

  const active = isTrace ? traceQ : runsQ; // en la vista por misión, la lista tiene su propia consulta
  const total = isTrace ? traceQ.data?.total ?? 0 : runsQ.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rows = runsQ.data?.rows ?? [];
  const syncedAt = Math.min(...[active.dataUpdatedAt, sumQ.dataUpdatedAt].filter(Boolean)) || 0;
  const failed = active.isError || sumQ.isError;

  // Si el conjunto se encoge y la página pedida ya no existe, se vuelve a la última (sin perder filtros).
  useEffect(() => { if (active.data && !active.isPlaceholderData && f.page > pages) set({ page: pages }); }, [active.data, active.isPlaceholderData, f.page, pages, set]);

  /* Posición: se guarda al desplazar y se recupera al volver desde un detalle con la misma URL. */
  const lastTop = useRef(0);
  useEffect(() => {
    const el = rootRef.current?.closest("main") as HTMLElement | null;
    if (!el) return;
    const on = () => { lastTop.current = el.scrollTop; };
    el.addEventListener("scroll", on, { passive: true });
    return () => { el.removeEventListener("scroll", on); try { sessionStorage.setItem(SCROLL_KEY, JSON.stringify({ search: location.search, top: lastTop.current })); } catch { /* sin almacenamiento */ } };
  }, []);
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current || !active.data) return;
    restored.current = true;
    try {
      const s = JSON.parse(sessionStorage.getItem(SCROLL_KEY) ?? "null") as { search: string; top: number } | null;
      if (s && s.search === location.search) requestAnimationFrame(() => { const el = rootRef.current?.closest("main") as HTMLElement | null; if (el) el.scrollTop = s.top; });
    } catch { /* sin almacenamiento */ }
  }, [active.data]);

  const repoOptions = (sum?.facets.repositories ?? []).filter((r) => !f.project || r.projectId === f.project);
  const filtersSet = [f.q, f.project, f.repo, f.phase, f.provider, f.status].filter(Boolean).length + (f.period !== DEFAULT_PERIOD && f.view !== "active" ? 1 : 0);
  const clear = () => { setQInput(""); set({ q: "", project: "", repo: "", phase: "", provider: "", status: "", period: DEFAULT_PERIOD }); };
  const setView = (v: View) => set({ view: v, status: "", run: "", mission: "", cat: "", kind: v === "trace" ? f.kind : "" });
  const periodLabel = PERIODS.find((p) => p[0] === f.period)?.[1].toLowerCase();

  return (
    <div ref={rootRef} className="mx-auto w-full max-w-[1400px] px-4 pb-24 pt-5 md:px-8 md:pb-12 md:pt-8">
      <Eyebrow>Ejecución</Eyebrow>
      <div className="mt-2 flex flex-wrap items-end justify-between gap-x-6 gap-y-1">
        <h1 className="m-0 font-display text-[32px] font-medium leading-[1.1] tracking-[-0.025em]">Agentes</h1>
        <div className="text-[12px] text-mu" aria-live="polite">Última sincronización: <b className="font-mono">{clock(syncedAt)}</b>{active.isFetching && <span> · actualizando…</span>}</div>
      </div>
      <p className="mb-4 mt-2 max-w-[75ch] text-[14px] text-mu">Aquí se ve qué se está ejecutando, dónde y qué necesita atención. El avance de cada misión está en <Link to="/missions" className="text-ok hover:underline">Misiones</Link>. Un <b>agente</b> es un contenedor persistente; la <b>ejecución</b> es cada run.</p>

      {failed && (
        <div role="alert" className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-card border border-er/60 px-4 py-3 text-[14px]">
          <span>No se pudo actualizar{active.isError ? ` la lista (${errText(active.error)})` : ` el resumen (${errText(sumQ.error)})`}. {active.data ? `Se muestran los datos de las ${clock(syncedAt)}.` : "Todavía no hay datos que mostrar."}</span>
          <Button size="sm" onClick={() => { void active.refetch(); void sumQ.refetch(); }}><RefreshCw className="h-4 w-4" />Volver a consultar</Button>
        </div>)}

      {/* resumen compacto: solo cifras verificables, cada una con su ámbito */}
      <div className="mb-4 flex snap-x gap-2 overflow-x-auto pb-1 lg:grid lg:grid-cols-5 lg:overflow-visible lg:pb-0" aria-label="Resumen">
        {!sum && !sumQ.isError && [0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-[76px] min-w-[168px] shrink-0 lg:min-w-0" />)}
        {sumQ.isError && !sum && <div className="col-span-full text-[13px] text-mu">Resumen no disponible.</div>}
        {sum && (<>
          <Stat label="Ejecuciones en curso" value={fmtInt(sum.counts.active)} note="runs vivos que cumplen los filtros" />
          <Stat label="Incidencias" value={fmtInt(sum.counts.issues)} tone={sum.counts.issues ? "bad" : undefined} note={`con error o caducadas · ${periodLabel}`} />
          <Stat label="Capacidad utilizada" value={`${fmtInt(sum.capacity.activeRuns)} / ${fmtInt(sum.capacity.limit)}`} note="runs activos / límite · toda la fábrica, sin filtros" />
          <Stat label="Misiones esperando capacidad" value={fmtInt(sum.capacity.waitingMissions.count)} tone={sum.capacity.waitingMissions.count ? "warn" : undefined} note="según la empresa seleccionada" />
          <Stat label="Esperan decisión humana" value={fmtInt(sum.humanApproval.missions)} note="misiones; no son incidencias"
            action={sum.humanApproval.missions > 0 ? <Link to="/missions?status=approval" className="text-ok hover:underline">Ver en Misiones</Link> : undefined} />
        </>)}
      </div>

      <div role="tablist" aria-label="Vistas" className="mb-3 flex flex-wrap gap-2">
        {VIEWS.map((v) => {
          const n = v === "trace" ? sum?.traceability.total : v === "missions" ? undefined : sum?.counts[v];
          return (
            <button key={v} role="tab" type="button" aria-selected={f.view === v} onClick={() => setView(v)}
              className={cn("min-h-11 cursor-pointer rounded-[10px] border px-4 text-[14px] font-medium", f.view === v ? "border-ac text-ok" : "border-ln2 text-tx hover:bg-hv")}>
              {VIEW_LABEL[v]}{n != null && <span className={cn("ml-2 font-mono text-[13px]", v === "issues" && n > 0 ? "text-er" : v === "trace" && n > 0 ? "text-wn" : "text-mu")}>{n}</span>}
            </button>);
        })}
      </div>
      <p className="m-0 mb-3 max-w-[85ch] text-[13px] text-mu">{VIEW_HELP[f.view]}</p>
      {f.view === "issues" && sum && sum.humanApproval.missions > 0 && (
        <p className="m-0 mb-3 rounded-card border border-ln px-3 py-2 text-[13px]">{sum.humanApproval.missions} misión(es) esperan una decisión humana. No son incidencias de ejecución: <Link to="/missions?status=approval" className="text-ok hover:underline">revisarlas en Misiones</Link>.</p>)}

      {!isTrace && !isMissions && (
        <>
          <div className="mb-2 md:hidden">
            <Button size="sm" variant="secondary" onClick={() => setShowFilters((s) => !s)} aria-expanded={showFilters}><ListFilter className="h-4 w-4" />Filtros{filtersSet ? ` (${filtersSet})` : ""}</Button>
          </div>
          <div className={cn("mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-[minmax(220px,1.4fr)_repeat(6,minmax(0,1fr))_auto]", showFilters ? "grid" : "hidden md:grid")} role="search">
            <Input type="search" aria-label="Buscar por misión" placeholder="Buscar misión (clave o título)" value={qInput} onChange={(e) => setQInput(e.target.value)} className="sm:col-span-2 lg:col-span-1" />
            <FilterSelect aria-label="Proyecto" value={f.project} onChange={(e) => set({ project: e.target.value, repo: "" })}>
              <option value="">Proyecto: todos</option>{sum?.facets.projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </FilterSelect>
            <FilterSelect aria-label="Repositorio" value={f.repo} onChange={(e) => set({ repo: e.target.value })}>
              <option value="">Repo: todos</option>{repoOptions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </FilterSelect>
            <FilterSelect aria-label="Fase" value={f.phase} onChange={(e) => set({ phase: e.target.value })}>
              <option value="">Fase: todas</option>{PHASES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </FilterSelect>
            <FilterSelect aria-label="Proveedor" value={f.provider} onChange={(e) => set({ provider: e.target.value })}>
              <option value="">Proveedor: todos</option>{sum?.providers.map((p) => <option key={p} value={p}>{p}</option>)}
            </FilterSelect>
            <FilterSelect aria-label="Estado" value={f.status} onChange={(e) => set({ status: e.target.value })}>
              <option value="">Estado: todos</option>{STATUS_BY_VIEW[f.view].map((s) => <option key={s} value={s}>{RUN_LABEL[s]}</option>)}
            </FilterSelect>
            <FilterSelect aria-label="Periodo" value={f.period} disabled={f.view === "active"} title={f.view === "active" ? "En curso muestra siempre todo lo que sigue vivo" : undefined} onChange={(e) => set({ period: e.target.value as Filters["period"] })}>
              {PERIODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </FilterSelect>
            {filtersSet > 0 && <Button variant="ghost" onClick={clear}>Limpiar</Button>}
          </div>
        </>)}

      {/* contenido */}
      {isMissions ? <MissionsCenter f={f} set={set} cid={cid} now={now} /> : isTrace ? (
        <>
          {sum && <div className="mb-3"><TraceKinds counts={traceQ.data?.counts ?? sum.traceability.byKind} kind={traceKind} onPick={(k) => set({ kind: k })} /></div>}
          {traceQ.isLoading && <Skeleton className="h-40" />}
          {traceQ.data && !traceQ.data.items.length && <div className="rounded-card border border-ln bg-sf"><Empty>Sin discrepancias de este tipo con la selección actual.</Empty></div>}
          {traceQ.data && traceQ.data.items.length > 0 && <TraceTable kind={traceKind} items={traceQ.data.items} onOpenRun={(id) => set({ run: id }, { keepPage: true })} />}
          {sum && companyId && <p className="mb-0 mt-3 text-[12px] text-mu">Con una empresa seleccionada solo se ven los registros atribuibles a ella; el consumo o los agentes sin proyecto/misión solo aparecen con «Todas las empresas».</p>}
        </>
      ) : (
        <>
          {runsQ.isLoading && <div className="flex flex-col gap-2" aria-busy="true">{[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-14" />)}</div>}
          {runsQ.data && !rows.length && (
            <div className="rounded-card border border-ln bg-sf"><Empty>
              {filtersSet ? <>Ningún run coincide con los filtros. <button type="button" onClick={clear} className="cursor-pointer text-ok hover:underline">Limpiar filtros</button></>
                : f.view === "active" ? "No hay ninguna ejecución en curso. Los agentes persistentes pueden seguir existiendo sin ejecutar nada."
                : f.view === "issues" ? `Sin incidencias de ejecución (${periodLabel}).` : `Sin runs terminados (${periodLabel}).`}
            </Empty></div>)}
          {rows.length > 0 && <div className={cn(runsQ.isPlaceholderData && "opacity-60 transition-opacity")}><RunsTable rows={rows} now={now} onOpen={(id) => set({ run: id }, { keepPage: true })} selectedId={f.run} /></div>}
        </>)}

      {total > 0 && !isMissions && (
        <nav className="mt-3 flex flex-wrap items-center justify-between gap-2 text-[13px] text-mu" aria-label="Paginación">
          <span>Mostrando {fmtInt((f.page - 1) * PAGE_SIZE + 1)}–{fmtInt(Math.min(total, f.page * PAGE_SIZE))} de {fmtInt(total)}</span>
          <span className="flex items-center gap-2">
            <Button size="sm" disabled={f.page <= 1} onClick={() => set({ page: f.page - 1 })} aria-label="Página anterior"><ChevronLeft className="h-4 w-4" />Anterior</Button>
            <span>Página {f.page} de {pages}</span>
            <Button size="sm" disabled={f.page >= pages} onClick={() => set({ page: f.page + 1 })} aria-label="Página siguiente">Siguiente<ChevronRight className="h-4 w-4" /></Button>
          </span>
        </nav>)}

      <MissionReconstructionDialog missionId={f.mission} onClose={() => { if (f.mission) set({ mission: "" }, { keepPage: true }); }} />
      <RunDetailDialog runId={f.run} now={now} onClose={() => { if (f.run) set({ run: "" }, { keepPage: true }); }} onOpenRun={(id) => set({ run: id }, { keepPage: true })} />
    </div>
  );
}

function Stat({ label, value, note, tone, action }: { label: string; value: string; note: string; tone?: "bad" | "warn"; action?: React.ReactNode }) {
  return (
    <div className="min-w-[168px] shrink-0 snap-start rounded-card border border-ln bg-sf px-3.5 py-3 lg:min-w-0">
      <div className="font-mono text-[11px] font-bold uppercase tracking-[0.12em] text-mu">{label}</div>
      <div className={cn("mt-1 font-display text-[26px] font-medium leading-none", tone === "bad" && "text-er", tone === "warn" && "text-wn")}>{value}</div>
      <div className="mt-1.5 text-[12px] leading-snug text-mu">{note}{action && <> · {action}</>}</div>
    </div>
  );
}
